import { ThemePageTypeEnum, type ThemePageType as PageType } from './schemas/theme.schema';

// Which theme page an address opens. The address bar is this storefront's only router, so every link a
// theme draws lands here: a shop route opens its page, a merchant's handle opens the page they wrote, and
// an address the shop has nothing for opens the not-found page rather than quietly drawing the home page.
// Shared, so the API that writes a store page reads the same page off the same address as the storefront.

// Reports whether a value names one of the theme pages.
const isPageType = (value: string | null | undefined): value is PageType => ThemePageTypeEnum.safeParse(value).success;

/** The handle of the merchant page drawn as the contact page, with the store's details and a form. */
export const CONTACT_SLUG = 'contact';

/** Where a merchant's own page is reached: the path a theme's footer links, `/pages/<handle>`, or Shopify's
 * `/policies/<handle>`, so a store that moved from Shopify keeps its policy addresses. */
const CONTENT_PATH = /^\/(?:pages|policies)\/([^/?#]+)\/?$/;

// Names the merchant page an address asks for, by its footer path or the preview's query, else null.
export function contentPageSlug(pathname: string, search: string): string | null {
  const matched = CONTENT_PATH.exec(pathname);
  if (matched) return decodeURIComponent(matched[1]);
  const params = new URLSearchParams(search);
  const asked = params.get('page');
  return params.get('slug') || params.get('article') || (asked && !isPageType(asked) ? asked : null);
}

/** The shop routes a theme's own links point at, and the page each one opens. */
const ROUTE_PAGES: ReadonlyArray<readonly [RegExp, PageType]> = [
  [/^\/(?:index\.html)?$/, 'home'],
  // The cart has a page of its own, as Shopify's /cart does; the drawer stays the quick look from any page.
  [/^\/cart\/?$/, 'cart'],
  // Every collection the store keeps, as Shopify's /collections lists them; one of them is a collection page.
  [/^\/collections\/?$/, 'collections-list'],
  [/^\/collections\//, 'collection'],
  [/^\/checkout\/?$/, 'checkout'],
  // Where a placed order is confirmed, as Shopify's order status page has an address of its own.
  [/^\/thank-you\/?$/, 'thankyou'],
  [/^\/products\/[^/]+\/?$/, 'product'],
  [/^\/search\/?$/, 'search'],
  // Where a shopper finds their order while the shop has no accounts; Shopify's account links land here.
  [/^\/account(?:\/login)?\/?$/, 'account']
];

// Maps an address onto a theme page: ?page=, a merchant's handle, a shop route, ?product=, else not-found.
export const requestedPage = (pathname: string, search: string): PageType => {
  const params = new URLSearchParams(search);
  const asked = params.get('page');
  if (isPageType(asked)) return asked;
  const slug = contentPageSlug(pathname, search);
  // The merchant's page at /pages/contact is the contact page, as Shopify's default contact page is.
  if (slug) return slug === CONTACT_SLUG ? 'contact' : 'article';
  const route = ROUTE_PAGES.find(([pattern]) => pattern.test(pathname))?.[1];
  if (route && route !== 'home') return route;
  if (params.get('product')) return 'product';
  // An address the shop has no page for says so, instead of quietly drawing the home page.
  return route ?? 'not-found';
};
